import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { seoHead } from "@/lib/seo";
import { ResourceShareBlock } from "@/components/ResourceShareBlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { CTABlock } from "@/components/CTABlock";
import { useBusinessConfig } from "@/lib/use-business-config";

const SECTIONS: { title: string; items: string[] }[] = [
  {
    title: "Daily driver checks (before your first job)",
    items: [
      "Walk round the car — any new dents, scuffs or panel damage",
      "All lights working — headlights, brakes, indicators, reverse",
      "Tyre pressures look correct, no obvious bulges",
      "Windscreen washers topped up, wipers clearing cleanly",
      "No new warning lights on the dash after start-up",
      "Interior clean, seatbelts retract, child locks set",
    ],
  },
  {
    title: "Weekly checks (set a reminder)",
    items: [
      "Tread depth across all four tyres (≥ 2mm if doing high mileage)",
      "Oil and coolant levels in the engine bay",
      "Brake feel under firm braking — no pulling, no grinding",
      "Handbrake holds on a slope",
      "Check for fluid leaks where you park",
      "Listen for new knocks, rattles or droning",
    ],
  },
  {
    title: "Before MOT or renewal deadline",
    items: [
      "Work through the MOT Pass Checklist (lights, tyres, brakes, wipers)",
      "Clear any dashboard warning lights with a diagnostic check",
      "Book MOT 5–28 days early so renewal date doesn't change",
      "Have V5C, insurance and PCO/licence documents to hand",
    ],
  },
  {
    title: "High-mileage warning signs",
    items: [
      "Tyres wearing unevenly or quickly on one corner",
      "Brakes feeling soft, grinding, or pulling to one side",
      "Suspension knocks over speed bumps or potholes",
      "Engine management or DPF/EGR warning lights",
      "Exhaust smoke (blue = oil, black = fuel, white = coolant)",
      "Bodywork scuffs that could affect rear-view or passenger safety",
    ],
  },
  {
    title: "When to call a garage",
    items: [
      "Any red warning light on the dash",
      "Brake or steering feel changes",
      "Coolant temperature rising or oil warning",
      "Unusual smell — burning, fuel or coolant",
      "Anything that could put a passenger at risk",
    ],
  },
  {
    title: "Downtime-reduction tips",
    items: [
      "Book first or last slot of the day to reduce lost earnings",
      "Combine MOT, service and any small fixes in one visit",
      "Keep a spare bulb kit and tyre repair gel in the boot",
      "Build a relationship with one garage — faster diagnosis next time",
    ],
  },
];

const FAQS = [
  {
    q: "Do you do TfL PCO inspections?",
    a: "Woodseer Autos is a DVSA-authorised MOT station, not a TfL PCO inspection centre. We help PCO and private-hire drivers with MOT, servicing, diagnostics and repairs to keep their vehicle in good working order. For TfL-specific PCO inspections, book at a TfL-approved centre and call us if you need help with what they flag.",
  },
  {
    q: "Can you get me back on the road quickly?",
    a: "Call us first thing — we'll tell you honestly whether it's a same-day fix or needs a parts order. We know lost earnings matter for PCO drivers.",
  },
  {
    q: "Do you do diesel DPF and EGR work?",
    a: "Yes — see our dedicated diesel DPF / EGR page for common warning lights, cleaning, regeneration and repair options.",
  },
];

export const Route = createFileRoute("/resources/pco-driver-vehicle-checklist")({
  head: () =>
    seoHead({
      path: "/resources/pco-driver-vehicle-checklist",
      title: "Free PCO Driver Vehicle Checklist | MOT & Safety Checks",
      description:
        "Free vehicle checklist for PCO, Uber, Bolt and private-hire drivers: tyres, brakes, lights, warning lights, servicing and MOT prep.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Free Resources", path: "/free-car-resources" },
        { name: "PCO Driver Vehicle Checklist", path: "/resources/pco-driver-vehicle-checklist" },
      ],
      jsonLd: [faqSchema(FAQS) as Record<string, unknown>],
    }),
  component: PCOChecklistResource,
});

function PCOChecklistResource() {
  const biz = useBusinessConfig();
  return (
    <>
      <section className="bg-foreground text-background py-14 md:py-20 no-print">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Free resource</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">
            PCO / Private-Hire Driver Vehicle Checklist
          </h1>
          <p className="mt-4 text-base text-background/70 leading-relaxed max-w-2xl">
            Practical checks for Uber, Bolt, minicab, private-hire and high-mileage drivers — to help you
            stay safe, pass MOTs and reduce expensive downtime.
          </p>
          <a
            href={biz.phoneHref}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
        </div>
      </section>

      <div className="print-only px-6 pt-6 text-foreground">
        <p className="text-xl font-extrabold">Woodseer Autos — PCO Driver Vehicle Checklist</p>
        <p className="text-sm">{NAP.phone} · woodseerautos.co.uk · 14 Copenhagen Place, London E14 7DF</p>
      </div>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 md:px-6 space-y-5">
          {SECTIONS.map((s) => (
            <div key={s.title} className="rounded-xl border border-border bg-card p-5 md:p-6">
              <h2 className="text-base md:text-lg font-bold text-foreground">{s.title}</h2>
              <ul className="mt-3 space-y-2">
                {s.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                    <span
                      aria-hidden
                      className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-sm border-2 border-foreground/40"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="rounded-xl border border-border bg-secondary/40 p-5 md:p-6 text-sm text-foreground">
            <p className="font-semibold">Helpful Woodseer pages for PCO drivers:</p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-primary">
              <Link to="/pco-mot-east-london" className="font-semibold hover:underline">PCO MOT →</Link>
              <Link to="/garage-for-uber-bolt-drivers-east-london" className="font-semibold hover:underline">Uber & Bolt garage →</Link>
              <Link to="/uber-mot-east-london" className="font-semibold hover:underline">Uber MOT →</Link>
              <Link to="/diesel-dpf-egr-east-london" className="font-semibold hover:underline">Diesel DPF / EGR →</Link>
              <Link to="/mot" className="font-semibold hover:underline">MOT →</Link>
              <Link to="/servicing" className="font-semibold hover:underline">Servicing →</Link>
              <Link to="/diagnostics" className="font-semibold hover:underline">Diagnostics →</Link>
              <Link to="/book" className="font-semibold hover:underline">Book →</Link>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={FAQS} title="PCO checklist — frequently asked questions" />
      <ResourceShareBlock path="/resources/pco-driver-vehicle-checklist" title="PCO Driver Vehicle Checklist" />
      <CTABlock headline="Need a fast turnaround?" subtext="Call us first — we'll be honest about same-day vs parts wait." />
    </>
  );
}
